
export class vec2 {
  constructor(
    readonly x: number,
    readonly y: number,
  ) { }

  static readonly zero: vec2 = new vec2(0, 0)

    ;
  *[Symbol.iterator]() {
    yield this.x
    yield this.y
  }

  get tuple(): [number, number] {
    return [this.x, this.y]
  }

  get debugDescription(): string {
    return `[${Array.from(this).map(v => v.toFixed(9)).join(', ')}]`
  }

  plus(that: vec2): vec2 {
    return new vec2(this.x + that.x, this.y + that.y)
  }

  minus(that: vec2): vec2 {
    return new vec2(this.x - that.x, this.y - that.y)
  }

  times(scalar: number): vec2 {
    return new vec2(this.x * scalar, this.y * scalar)
  }

  dot(that: vec2): number {
    return this.x * that.x + this.y * that.y
  }

  is(that: vec2): boolean {
    return Object.is(this.x, that.x) && Object.is(this.y, that.y)
  }

  get lengthSquared(): number {
    return this.dot(this)
  }

  get length(): number {
    return Math.hypot(this.x, this.y)
  }

  get normalized(): vec2 {
    const length = Math.hypot(this.x, this.y)
    return new vec2(this.x / length, this.y / length)
  }
}

export type TimesType_mat2<T> = T extends number ? mat2 : T extends vec2 ? vec2 : mat2

export class mat2 {
  // OpenGL uses column-major order, so I do too.

  private constructor(
    // Column 0
    readonly m00: number, readonly m10: number,

    // Column 1
    readonly m01: number, readonly m11: number,
  ) { }

  // OpenGL-style.
  static columnMajor(
    // Column 0
    m00: number, m10: number,

    // Column 1
    m01: number, m11: number,
  ): mat2 {
    return new mat2(
      m00, m10,
      m01, m11,
    )
  }

  // Mathematics-style.
  static rowMajor(
    // Row 0
    m00: number, m01: number,

    // Row 1
    m10: number, m11: number,
  ): mat2 {
    return new mat2(
      m00, m10,
      m01, m11,
    )
  }

  static readonly identity: mat2 = new mat2(
    1, 0,
    0, 1,
  )

    ;
  // All components in column-major (OpenGL) order.
  *[Symbol.iterator]() {
    yield this.m00
    yield this.m10
    yield this.m01
    yield this.m11
  }

  get debugDescription(): string {
    return Array.from(this.transposed).reduce((answer, v, i) => {
      const sep = i === 0 ? '' : i % 2 === 0 ? '\n ' : ', '
      const signPad = v >= 0 ? ' ' : ''
      return `${answer}${sep}${signPad}${v.toFixed(9)}`
    }, '[') + ']'
  }

  plus(that: mat2): mat2 {
    return mat2.columnMajor(
      this.m00 + that.m00, this.m10 + that.m10,
      this.m01 + that.m01, this.m11 + that.m11,
    )
  }

  minus(that: mat2): mat2 {
    return mat2.columnMajor(
      this.m00 - that.m00, this.m10 - that.m10,
      this.m01 - that.m01, this.m11 - that.m11,
    )
  }

  /**
   * `this * that`
   */
  times<T extends number | vec2 | mat2>(that: T): TimesType_mat2<T> {
    const { m00, m10, m01, m11 } = this
    // I don't just say `that instanceof mat2` because that doesn't work after a hot reload of this file.
    if (Object.getPrototypeOf(that).constructor.name === mat2.name) {
      const [r00, r10, r01, r11] = [...(that as mat2)]
      return mat2.columnMajor(
        m00 * r00 + m01 * r10, m10 * r00 + m11 * r10,
        m00 * r01 + m01 * r11, m10 * r01 + m11 * r11,
      ) as TimesType_mat2<T>
    } else if (Object.getPrototypeOf(that).constructor.name === vec2.name) {
      const { x, y } = that as vec2
      return new vec2(
        m00 * x + m01 * y,
        m10 * x + m11 * y,
      ) as TimesType_mat2<T>
    } else {
      const scalar = that as number
      return mat2.columnMajor(
        m00 * scalar, m10 * scalar,
        m01 * scalar, m11 * scalar,
      ) as TimesType_mat2<T>
    }
  }

  get transposed(): mat2 {
    return mat2.columnMajor(
      this.m00, this.m01,
      this.m10, this.m11,
    )
  }
}

export class vec3 {
  constructor(
    readonly x: number,
    readonly y: number,
    readonly z: number,
  ) { }

  ;
  *[Symbol.iterator]() {
    yield this.x
    yield this.y
    yield this.z
  }

  get tuple(): [number, number, number] {
    return [this.x, this.y, this.z]
  }

  get debugDescription(): string {
    return `[${Array.from(this).map(v => v.toFixed(9)).join(', ')}]`
  }

  plus(that: vec3): vec3 {
    return new vec3(this.x + that.x, this.y + that.y, this.z + that.z)
  }

  minus(that: vec3): vec3 {
    return new vec3(this.x - that.x, this.y - that.y, this.z - that.z)
  }

  times(scalar: number): vec3 {
    return new vec3(this.x * scalar, this.y * scalar, this.z * scalar)
  }

  cross(that: vec3): vec3 {
    const { x: x1, y: y1, z: z1 } = this
    const { x: x2, y: y2, z: z2 } = that
    return new vec3(
      y1 * z2 - z1 * y2,
      z1 * x2 - x1 * z2,
      x1 * y2 - y1 * x2,
    )
  }

  dot(that: vec3): number {
    return this.x * that.x + this.y * that.y + this.z * that.z
  }

  static readonly zero: vec3 = new vec3(0, 0, 0)
  static readonly infinity: vec3 = new vec3(Infinity, Infinity, Infinity)

  static readonly xPlus: vec3 = new vec3(1, 0, 0)
  static readonly xMinus: vec3 = new vec3(-1, 0, 0)
  static readonly yPlus: vec3 = new vec3(0, 1, 0)
  static readonly yMinus: vec3 = new vec3(0, -1, 0)
  static readonly zPlus: vec3 = new vec3(0, 0, 1)
  static readonly zMinus: vec3 = new vec3(0, 0, -1)

  get normalized(): vec3 {
    const length = Math.hypot(this.x, this.y, this.z)
    return new vec3(this.x / length, this.y / length, this.z / length)
  }

  get xy(): vec2 {
    return new vec2(this.x, this.y)
  }

  get asPoint(): vec4 {
    return new vec4(this.x, this.y, this.z, 1)
  }

  get asVector(): vec4 {
    return new vec4(this.x, this.y, this.z, 0)
  }

  get lengthSquared(): number {
    return this.dot(this)
  }

  get length(): number {
    return Math.hypot(this.x, this.y, this.z)
  }

  get negated(): vec3 {
    return new vec3(-this.x, -this.y, -this.z)
  }

  static arrayFromFloat32s(array: Float32Array): vec3[] {
    const result: vec3[] = []
    for (let i = 0; i < array.length; i += 3) {
      result.push(new vec3(array[i], array[i + 1], array[i + 2]))
    }
    return result
  }

  static * yieldVec3s(array: Float32Array): Generator<vec3, void, unknown> {
    for (let i = 0, end = array.length - 2; i < end; i += 3) {
      yield new vec3(array[i], array[i + 1], array[i + 2])
    }
  }
}

export type TimesType_mat3<T> = T extends number ? mat3 : T extends vec3 ? vec3 : mat3

export class mat3 {
  // OpenGL uses column-major order, so I do too.

  private constructor(
    // Column 0
    readonly m00: number, readonly m10: number, readonly m20: number,

    // Column 1
    readonly m01: number, readonly m11: number, readonly m21: number,

    // Column 2
    readonly m02: number, readonly m12: number, readonly m22: number,
  ) { }

  // OpenGL-style.
  static columnMajor(
    // Column 0
    m00: number, m10: number, m20: number,

    // Column 1
    m01: number, m11: number, m21: number,

    // Column 2
    m02: number, m12: number, m22: number,
  ): mat3 {
    return new mat3(
      m00, m10, m20,
      m01, m11, m21,
      m02, m12, m22,
    )
  }

  // Mathematics-style.
  static rowMajor(
    // Row 0
    m00: number, m01: number, m02: number,

    // Row 1
    m10: number, m11: number, m12: number,

    // Row 2
    m20: number, m21: number, m22: number,
  ): mat3 {
    return new mat3(
      m00, m10, m20,
      m01, m11, m21,
      m02, m12, m22,
    )
  }

  static readonly identity: mat3 = new mat3(
    1, 0, 0,
    0, 1, 0,
    0, 0, 1,
  )

  static readonly zero: mat3 = new mat3(
    0, 0, 0,
    0, 0, 0,
    0, 0, 0,
  )

    ;
  // All components in column-major (OpenGL) order.
  *[Symbol.iterator]() {
    yield this.m00
    yield this.m10
    yield this.m20
    yield this.m01
    yield this.m11
    yield this.m21
    yield this.m02
    yield this.m12
    yield this.m22
  }

  get debugDescription(): string {
    return Array.from(this.transposed).reduce((answer, v, i) => {
      const sep = i === 0 ? '' : i % 3 === 0 ? '\n ' : ', '
      const signPad = v >= 0 ? ' ' : ''
      return `${answer}${sep}${signPad}${v.toFixed(9)}`
    }, '[') + ']'
  }

  plus(that: mat3): mat3 {
    return mat3.columnMajor(
      this.m00 + that.m00, this.m10 + that.m10, this.m20 + that.m20,
      this.m01 + that.m01, this.m11 + that.m11, this.m21 + that.m21,
      this.m02 + that.m02, this.m12 + that.m12, this.m22 + that.m22,
    )
  }

  minus(that: mat3): mat3 {
    return mat3.columnMajor(
      this.m00 - that.m00, this.m10 - that.m10, this.m20 - that.m20,
      this.m01 - that.m01, this.m11 - that.m11, this.m21 - that.m21,
      this.m02 - that.m02, this.m12 - that.m12, this.m22 - that.m22,
    )
  }

  /**
   * `this * that`
   */
  times<T extends number | vec3 | mat3>(that: T): TimesType_mat3<T> {
    const { m00, m10, m20, m01, m11, m21, m02, m12, m22 } = this
    if (Object.getPrototypeOf(that).constructor.name === mat3.name) {
      const [r00, r10, r20, r01, r11, r21, r02, r12, r22] = [...(that as mat3)]
      return mat3.columnMajor(
        // Column 0
        m00 * r00 + m01 * r10 + m02 * r20,
        m10 * r00 + m11 * r10 + m12 * r20,
        m20 * r00 + m21 * r10 + m22 * r20,

        // Column 1
        m00 * r01 + m01 * r11 + m02 * r21,
        m10 * r01 + m11 * r11 + m12 * r21,
        m20 * r01 + m21 * r11 + m22 * r21,

        // Column 2
        m00 * r02 + m01 * r12 + m02 * r22,
        m10 * r02 + m11 * r12 + m12 * r22,
        m20 * r02 + m21 * r12 + m22 * r22,
      ) as TimesType_mat3<T>
    } else if (Object.getPrototypeOf(that).constructor.name === vec3.name) {
      const { x, y, z } = that as vec3
      return new vec3(
        m00 * x + m01 * y + m02 * z,
        m10 * x + m11 * y + m12 * z,
        m20 * x + m21 * y + m22 * z,
      ) as TimesType_mat3<T>
    } else {
      const scalar = that as number
      return mat3.columnMajor(
        m00 * scalar, m10 * scalar, m20 * scalar,
        m01 * scalar, m11 * scalar, m21 * scalar,
        m02 * scalar, m12 * scalar, m22 * scalar,
      ) as TimesType_mat3<T>
    }
  }

  get transposed(): mat3 {
    return mat3.columnMajor(
      this.m00, this.m01, this.m02,
      this.m10, this.m11, this.m12,
      this.m20, this.m21, this.m22,
    )
  }

  column(c: number): vec3 {
    switch (c) {
    case 0:
      return new vec3(this.m00, this.m10, this.m20)
    case 1:
      return new vec3(this.m01, this.m11, this.m21)
    case 2:
      return new vec3(this.m02, this.m12, this.m22)
    default:
      throw new Error(`mat3.column: ${c} out of range`)
    }
  }

  get determinant(): number {
    const { m00, m10, m20, m01, m11, m21, m02, m12, m22 } = this
    return (0
      + m00 * (m11 * m22 - m12 * m21)
      + m10 * (m21 * m02 - m22 * m01)
      + m20 * (m01 * m12 - m02 * m11)
    )
  }

  get inverse(): mat3 {
    const s = 1.0 / this.determinant
    const { m00, m10, m20, m01, m11, m21, m02, m12, m22 } = this
    return mat3.columnMajor(
      s * (m11 * m22 - m12 * m21), s * (m12 * m20 - m22 * m10), s * (m10 * m21 - m20 * m11),
      s * (m21 * m02 - m01 * m22), s * (m22 * m00 - m02 * m20), s * (m20 * m01 - m00 * m21),
      s * (m01 * m12 - m11 * m02), s * (m02 * m10 - m12 * m00), s * (m00 * m11 - m10 * m01),
    )
  }

  /**
   * @returns A matrix suitable for transforming normals when the points are transformed by `this`. Transformed normals still need to be normalized unless `this` is orthonormal.
   */
  get normalTransform(): mat3 {
    // https://www.forwardscattering.org/post/62
    // I don't need to compute the full inverse transpose. The adjugate transpose times the sign of the determinant is sufficient.

    const s = Math.sign(this.determinant)
    if (s === 0) {
      // Non-invertible matrix.
      return mat3.zero
    }

    const { m00, m10, m20, m01, m11, m21, m02, m12, m22 } = this
    return mat3.rowMajor(
      s * (m11 * m22 - m12 * m21), s * (m12 * m20 - m22 * m10), s * (m10 * m21 - m20 * m11),
      s * (m21 * m02 - m01 * m22), s * (m22 * m00 - m02 * m20), s * (m20 * m01 - m00 * m21),
      s * (m01 * m12 - m11 * m02), s * (m02 * m10 - m12 * m00), s * (m00 * m11 - m10 * m01),
    )
  }

  get asMat4(): mat4 {
    return mat4.columnMajor(
      this.m00, this.m10, this.m20, 0,
      this.m01, this.m11, this.m21, 0,
      this.m02, this.m12, this.m22, 0,
      0, 0, 0, 1,
    )
  }
}

if (import.meta.vitest) {
  const { describe, it, expect } = import.meta.vitest

  describe('mat3', () => {
    it('should compute inverse correctly', () => {
      const m = mat3.columnMajor(
        1, 2, 3,
        -3, 1, 17,
        0.5, 0.125, 128,
      )
      const i = m.inverse
      expect([...i.times(m)]).toEqual([
        1, 0, 0,
        0, 1, 0,
        0, 0, 1,
      ].map(n => expect.closeTo(n, 7)))
    })
  })

  describe('mat3 should', () => {
    it('multiply correctly', () => {
      const q_45 = Quat.rotation(vec3.xPlus, 0.25 * Math.PI)
      const q_45_squared = q_45.times(q_45)
      const q_90 = Quat.rotation(vec3.xPlus, 0.5 * Math.PI)

      expect([...q_45_squared])
        .toEqual([...q_90].map(n => expect.closeTo(n, 7)))
    })
  })
}

export class vec4 {
  constructor(
    readonly x: number,
    readonly y: number,
    readonly z: number,
    readonly w: number,
  ) { }

  ;
  *[Symbol.iterator]() {
    yield this.x
    yield this.y
    yield this.z
    yield this.w
  }

  get tuple(): [number, number, number, number] {
    return [this.x, this.y, this.z, this.w]
  }

  get debugDescription(): string {
    return `[${Array.from(this).map(v => v.toFixed(9)).join(', ')}]`
  }

  plus(that: vec4): vec4 {
    return new vec4(this.x + that.x, this.y + that.y, this.z + that.z, this.w + that.w)
  }

  minus(that: vec4): vec4 {
    return new vec4(this.x - that.x, this.y - that.y, this.z - that.z, this.w - that.w)
  }

  times(scalar: number): vec4 {
    return new vec4(this.x * scalar, this.y * scalar, this.z * scalar, this.w * scalar)
  }

  dot(that: vec4): number {
    return this.x * that.x + this.y * that.y + this.z * that.z + this.w * that.w
  }

  get normalized(): vec4 {
    const length = Math.hypot(this.x, this.y, this.z, this.w)
    return new vec4(this.x / length, this.y / length, this.z / length, this.w / length)
  }

  static readonly zero: vec4 = new vec4(0, 0, 0, 0)

  static readonly origin: vec4 = new vec4(0, 0, 0, 1)

  get xy(): vec2 {
    return new vec2(this.x, this.y)
  }

  get xyz(): vec3 {
    return new vec3(this.x, this.y, this.z)
  }

  get lengthSquared(): number {
    return this.dot(this)
  }

  get length(): number {
    return Math.hypot(this.x, this.y, this.z, this.w)
  }

  get perspectiveDivided(): vec3 {
    const { w } = this
    return new vec3(this.x / w, this.y / w, this.z / w)
  }
}

export type TimesType_mat4<T> = T extends number ? mat4 : T extends vec4 ? vec4 : mat4

type OrthographicSpec = {
  left: number
  right: number
  bottom: number
  top: number
  near: number
  far: number
} | {
  width: number
  height: number
  depth: number
  center: vec3 | vec4
}

type PerspectiveSpec = {
  verticalFieldOfView: number

  /** width / height of the viewport */
  aspectRatio: number

  /** defaults to 0.001 */
  near?: number

  /** defaults to 1000 */
  far?: number
}

export class mat4 {
  // OpenGL uses column-major order, so I do too.

  private constructor(
    // Column 0
    readonly m00: number, readonly m10: number, readonly m20: number, readonly m30: number,

    // Column 1
    readonly m01: number, readonly m11: number, readonly m21: number, readonly m31: number,

    // Column 2
    readonly m02: number, readonly m12: number, readonly m22: number, readonly m32: number,

    // Column 3
    readonly m03: number, readonly m13: number, readonly m23: number, readonly m33: number,
  ) { }

  // OpenGL-style.
  static columnMajor(
    m00: number, m10: number, m20: number, m30: number,
    m01: number, m11: number, m21: number, m31: number,
    m02: number, m12: number, m22: number, m32: number,
    m03: number, m13: number, m23: number, m33: number,
  ): mat4 {
    return new mat4(
      m00, m10, m20, m30,
      m01, m11, m21, m31,
      m02, m12, m22, m32,
      m03, m13, m23, m33,
    )
  }

  // Mathematics-style.
  static rowMajor(
    m00: number, m01: number, m02: number, m03: number,
    m10: number, m11: number, m12: number, m13: number,
    m20: number, m21: number, m22: number, m23: number,
    m30: number, m31: number, m32: number, m33: number,
  ): mat4 {
    return new mat4(
      m00, m10, m20, m30,
      m01, m11, m21, m31,
      m02, m12, m22, m32,
      m03, m13, m23, m33,
    )
  }

  static readonly identity: mat4 = new mat4(
    1, 0, 0, 0,
    0, 1, 0, 0,
    0, 0, 1, 0,
    0, 0, 0, 1,
  )

  static readonly zero: mat4 = new mat4(
    0, 0, 0, 0,
    0, 0, 0, 0,
    0, 0, 0, 0,
    0, 0, 0, 0
  )

    ;
  // All components in column-major (OpenGL) order.
  *[Symbol.iterator]() {
    yield this.m00
    yield this.m10
    yield this.m20
    yield this.m30
    yield this.m01
    yield this.m11
    yield this.m21
    yield this.m31
    yield this.m02
    yield this.m12
    yield this.m22
    yield this.m32
    yield this.m03
    yield this.m13
    yield this.m23
    yield this.m33
  }

  get debugDescription(): string {
    return Array.from(this.transposed).reduce((answer, v, i) => {
      const sep = i === 0 ? '' : i % 4 === 0 ? ';\n ' : ' '
      const signPad = v >= 0 ? ' ' : ''
      return `${answer}${sep}${signPad}${v.toFixed(9)}`
    }, '[') + ']'
  }

  plus(that: mat4): mat4 {
    return mat4.columnMajor(
      this.m00 + that.m00, this.m10 + that.m10, this.m20 + that.m20, this.m30 + that.m30,
      this.m01 + that.m01, this.m11 + that.m11, this.m21 + that.m21, this.m31 + that.m31,
      this.m02 + that.m02, this.m12 + that.m12, this.m22 + that.m22, this.m32 + that.m32,
      this.m03 + that.m03, this.m13 + that.m13, this.m23 + that.m23, this.m33 + that.m33,
    )
  }

  minus(that: mat4): mat4 {
    return mat4.columnMajor(
      this.m00 - that.m00, this.m10 - that.m10, this.m20 - that.m20, this.m30 - that.m30,
      this.m01 - that.m01, this.m11 - that.m11, this.m21 - that.m21, this.m31 - that.m31,
      this.m02 - that.m02, this.m12 - that.m12, this.m22 - that.m22, this.m32 - that.m32,
      this.m03 - that.m03, this.m13 - that.m13, this.m23 - that.m23, this.m33 - that.m33,
    )
  }

  /**
   * `this * that`
   */
  times<T extends number | vec4 | mat4>(that: T): TimesType_mat4<T> {
    const { m00, m10, m20, m30, m01, m11, m21, m31, m02, m12, m22, m32, m03, m13, m23, m33 } = this
    if (Object.getPrototypeOf(that).constructor.name === mat4.name) {
      const [r00, r10, r20, r30, r01, r11, r21, r31, r02, r12, r22, r32, r03, r13, r23, r33] = [...(that as mat4)]
      return mat4.columnMajor(
        // Column 0
        m00 * r00 + m01 * r10 + m02 * r20 + m03 * r30,
        m10 * r00 + m11 * r10 + m12 * r20 + m13 * r30,
        m20 * r00 + m21 * r10 + m22 * r20 + m23 * r30,
        m30 * r00 + m31 * r10 + m32 * r20 + m33 * r30,

        // Column 1
        m00 * r01 + m01 * r11 + m02 * r21 + m03 * r31,
        m10 * r01 + m11 * r11 + m12 * r21 + m13 * r31,
        m20 * r01 + m21 * r11 + m22 * r21 + m23 * r31,
        m30 * r01 + m31 * r11 + m32 * r21 + m33 * r31,

        // Column 2
        m00 * r02 + m01 * r12 + m02 * r22 + m03 * r32,
        m10 * r02 + m11 * r12 + m12 * r22 + m13 * r32,
        m20 * r02 + m21 * r12 + m22 * r22 + m23 * r32,
        m30 * r02 + m31 * r12 + m32 * r22 + m33 * r32,

        // Column 3
        m00 * r03 + m01 * r13 + m02 * r23 + m03 * r33,
        m10 * r03 + m11 * r13 + m12 * r23 + m13 * r33,
        m20 * r03 + m21 * r13 + m22 * r23 + m23 * r33,
        m30 * r03 + m31 * r13 + m32 * r23 + m33 * r33,
      ) as TimesType_mat4<T>
    } else if (Object.getPrototypeOf(that).constructor.name === vec4.name) {
      const { x, y, z, w } = that as vec4
      return new vec4(
        m00 * x + m01 * y + m02 * z + m03 * w,
        m10 * x + m11 * y + m12 * z + m13 * w,
        m20 * x + m21 * y + m22 * z + m23 * w,
        m30 * x + m31 * y + m32 * z + m33 * w,
      ) as TimesType_mat4<T>
    } else {
      const scalar = that as number
      return mat4.columnMajor(
        m00 * scalar, m10 * scalar, m20 * scalar, m30 * scalar,
        m01 * scalar, m11 * scalar, m21 * scalar, m31 * scalar,
        m02 * scalar, m12 * scalar, m22 * scalar, m32 * scalar,
        m03 * scalar, m13 * scalar, m23 * scalar, m33 * scalar,
      ) as TimesType_mat4<T>
    }
  }

  get basis(): mat3 {
    return mat3.columnMajor(
      this.m00, this.m10, this.m20,
      this.m01, this.m11, this.m21,
      this.m02, this.m12, this.m22,
    )
  }

  get transposed(): mat4 {
    return mat4.columnMajor(
      this.m00, this.m01, this.m02, this.m03,
      this.m10, this.m11, this.m12, this.m13,
      this.m20, this.m21, this.m22, this.m23,
      this.m30, this.m31, this.m32, this.m33,
    )
  }

  get inverse(): mat4 {
    // *Foundations of Game Engine Development* §1.7.
    const a = new vec3(this.m00, this.m10, this.m20)
    const b = new vec3(this.m01, this.m11, this.m21)
    const c = new vec3(this.m02, this.m12, this.m22)
    const d = new vec3(this.m03, this.m13, this.m23)
    const { m30: x, m31: y, m32: z, m33: w } = this
    const s = a.cross(b)
    const t = c.cross(d)
    const u = a.times(y).minus(b.times(x))
    const v = c.times(w).minus(d.times(z))
    const det = s.dot(v) + t.dot(u)
    if (det === 0) {
      return mat4.zero
    }
    const ted = 1 / det
    const basisRow0 = b.cross(v).plus(t.times(y)).times(ted)
    const basisRow1 = v.cross(a).minus(t.times(x)).times(ted)
    const basisRow2 = d.cross(u).plus(s.times(w)).times(ted)
    const perspectiveRow = u.cross(c).minus(s.times(z)).times(ted)
    return mat4.rowMajor(
      ...basisRow0.tuple, b.dot(t) * -ted,
      ...basisRow1.tuple, a.dot(t) * ted,
      ...basisRow2.tuple, d.dot(s) * -ted,
      ...perspectiveRow.tuple, c.dot(s) * ted,
    )
  }

  static translation(t: vec3 | vec4): mat4 {
    return mat4.columnMajor(
      1, 0, 0, 0,
      0, 1, 0, 0,
      0, 0, 1, 0,
      t.x, t.y, t.z, 1,
    )
  }

  /**
   * @returns `mat4.translation(t).times(this)`. Note that the translation matrix is **on the left**.
   */
  translated(t: vec3 | vec4): mat4 {
    return mat4.translation(t).times(this)
  }

  static rotation(axis: vec3 | vec4, radians: number): mat4 {
    // https://en.wikipedia.org/wiki/Rotation_matrix#Rotation_matrix_from_axis_and_angle

    const cos = Math.cos(radians)
    // If radians is near zero, 1 - cos may not be very accurate. Use a trig identity to compute it more accurately.
    let iCos: number
    if (Math.abs(radians) >= 0.0625) {
      iCos = 1 - cos
    } else {
      const sin2 = Math.sin(radians / 2)
      iCos = 2 * sin2 * sin2
    }
    const sin = Math.sin(radians)

    const [ux, uy, uz] = axis.normalized

    return mat4.rowMajor(
      ux * ux * iCos + cos, uy * ux * iCos - uz * sin, uz * ux * iCos + uy * sin, 0,
      ux * uy * iCos + uz * sin, uy * uy * iCos + cos, uz * uy * iCos - ux * sin, 0,
      ux * uz * iCos - uy * sin, uy * uz * iCos + ux * sin, uz * uz * iCos + cos, 0,
      0, 0, 0, 1
    )
  }

  /**
   * @returns `mat4.rotation(axis, radians).times(this)`. Note that the rotation matrix is **on the left**.
   */
  rotated(axis: vec3 | vec4, radians: number): mat4 {
    return mat4.rotation(axis, radians).times(this)
  }

  static uniformScale(scale: number): mat4 {
    return mat4.columnMajor(
      scale, 0, 0, 0,
      0, scale, 0, 0,
      0, 0, scale, 0,
      0, 0, 0, 1,
    )
  }

  static scale(x: number, y: number, z: number): mat4 {
    return mat4.columnMajor(
      x, 0, 0, 0,
      0, y, 0, 0,
      0, 0, z, 0,
      0, 0, 0, 1,
    )
  }

  /**
   * @returns An orthographic projection matrix.
   */
  static orthographic(spec: OrthographicSpec): mat4 {
    const { width, height, depth, center } = 'width' in spec ? spec : {
      width: spec.right - spec.left,
      height: spec.top - spec.bottom,
      depth: spec.near - spec.far,
      center: new vec3(
        0.5 * (spec.left + spec.right),
        0.5 * (spec.bottom + spec.top),
        0.5 * (spec.near + spec.far),
      ),
    }

    // I negate z in clip space because the default depth test is GL_LESS, which favors depths closer to -∞. But since the camera looks down the negative z-axis, that favors fragments further from the camera unless I negate the depths.
    return mat4.columnMajor(
      2 / width, 0, 0, 0,
      0, 2 / height, 0, 0,
      0, 0, -2 / depth, 0,
      -2 * center.x / width, -2 * center.y / height, -2 * center.z / depth, 1,
    )
  }

  /**
   * @returns `mat4.orthographic(spec).times(this)`. Note that the orthographic projection matrix is **on the left**.
   */
  projectedOrthographically(spec: OrthographicSpec): mat4 {
    return mat4.orthographic(spec).times(this)
  }

  /**
   * @returns A perspective projection matrix.
   */
  static perspective(spec: PerspectiveSpec): mat4 {
    // Reverse-Z, infinite far plane.
    // https://iolite-engine.com/blog_posts/reverse_z_cheatsheet
    // https://developer.nvidia.com/content/depth-precision-visualized
    // https://geometry.caltech.edu/pubs/UD12.pdf

    const { verticalFieldOfView: yFov, aspectRatio, near = mat4.defaultNear } = spec
    // I need 1/tan(yFov/2). Here I use the identity 1/tan(θ) = tan(π/2 - θ) to avoid a division.
    const s = Math.tan(Math.PI * 0.5 - 0.5 * yFov)

    return mat4.columnMajor(
      s / aspectRatio, 0, 0, 0,
      0, s, 0, 0,
      0, 0, 0, -1,
      0, 0, -near, 0
    )
  }

  /**
   * @returns The inverse of my perspective projection matrix.
   */
  static inversePerspective(spec: PerspectiveSpec): mat4 {
    const { verticalFieldOfView: yFov, aspectRatio, near = mat4.defaultNear } = spec
    // Here I need 1/(1/tan(yFov/2)) = tan(yFov/2).
    const s = Math.tan(0.5 * yFov)

    return mat4.columnMajor(
      aspectRatio * s, 0, 0, 0,
      0, s, 0, 0,
      0, 0, 0, -1 / near,
      0, 0, -1, 0
    )
  }

  static readonly defaultNear: number = -0.1

  /**
   * @returns `mat4.persective(spec).times(this)`. Note that the perspective projection matrix is **on the left**.
   */
  projected(spec: PerspectiveSpec): mat4 {
    return mat4.perspective(spec).times(this)
  }
}

if (import.meta.vitest) {
  const { describe, it, expect } = import.meta.vitest

  describe('mat4 should', () => {
    it('multiply a mat4 by a mat4 correctly', () => {
      const a = mat4.columnMajor(
        1, 2, 3, 4,
        5, 6, 7, 8,
        9, 10, 11, 12,
        13, 14, 15, 16
      )

      const b = mat4.columnMajor(
        17, 18, 19, 20,
        21, 22, 23, 24,
        25, 26, 27, 28,
        29, 30, 31, 32
      )

      const actual = a.times(b)

      // Computed with octave.
      const expected = mat4.rowMajor(
        538, 650, 762, 874,
        612, 740, 868, 996,
        686, 830, 974, 1118,
        760, 920, 1080, 1240,
      )

      expect(actual).toEqual(expected)
    })

    it(`multiply a mat4 by a vec4 correctly`, () => {
      const a = mat4.columnMajor(
        17, 18, 19, 20,
        21, 22, 23, 24,
        25, 26, 27, 28,
        29, 30, 31, 32
      )

      const v = new vec4(33, 34, 35, 36)

      const actual = a.times(v)

      // Computed with octave.
      const expected = new vec4(3194, 3332, 3470, 3608)

      expect(actual).toEqual(expected)
    })

    it(`compute inversePerspective correctly`, () => {
      const spec: PerspectiveSpec = {
        verticalFieldOfView: 0.25 * Math.PI,
        aspectRatio: 1.5,
        near: 0.1,
      }
      const p = mat4.perspective(spec)
      const ip = mat4.inversePerspective(spec)
      const actual = Array.from(p.times(ip))
      expect(actual).toEqual([
        expect.closeTo(1, 7), 0, 0, 0,
        0, expect.closeTo(1, 7), 0, 0,
        0, 0, expect.closeTo(1, 7), 0,
        0, 0, 0, expect.closeTo(1, 7),
      ])
    })

    it(`should invert correctly`, () => {
      const a = mat4.rowMajor(
        8, -5, 1, -9,
        -6, 10, -3, 7,
        2, -8, 5, -1,
        -4, 3, 9, 6,
      )

      const i = a.inverse

      const actual = a.times(i)
      expect([...actual]).toEqual([...mat4.identity].map(v => expect.closeTo(v, 7)))
    })
  })
}

export class Ray {
  readonly direction: vec3

  constructor(
    readonly origin: vec3,
    direction: vec3
  ) {
    this.direction = direction.normalized
  }

  at(t: number): vec3 {
    return this.origin.plus(this.direction.times(t))
  }
}

export class Quat {
  constructor(
    readonly w: number,
    readonly x: number,
    readonly y: number,
    readonly z: number
  ) { }

  static identity: Quat = new Quat(1, 0, 0, 0)

    ;
  *[Symbol.iterator]() {
    yield this.w
    yield this.x
    yield this.y
    yield this.z
  }

  static rotation(axis: vec3, radians: number): Quat {
    const halfRadians = 0.5 * radians
    const c = Math.cos(halfRadians)
    const s = Math.sin(halfRadians)
    const { x, y, z } = axis.normalized
    return new Quat(c, s * x, s * y, s * z)
  }

  get lengthSquared(): number {
    return this.w * this.w + this.x * this.x + this.y * this.y + this.z * this.z
  }

  get length(): number {
    return Math.sqrt(this.lengthSquared)
  }

  get normalized(): Quat {
    const l = this.length
    return l === 0 ? this : new Quat(
      this.w / l, this.x / l, this.y / l, this.z / l
    )
  }

  get conjugate(): Quat {
    return new Quat(this.w, -this.x, -this.y, -this.z)
  }

  get inverse(): Quat {
    const ll = this.lengthSquared
    return new Quat(
      this.w / ll, -this.x / ll, -this.y / ll, -this.z / ll
    )
  }

  get mat4(): mat4 {
    const { w, x, y, z } = this
    const xx = x * x, yy = y * y, zz = z * z
    const wx = w * x, wy = w * y, wz = w * z
    const yz = y * z, zx = x * z, xy = x * y
    return mat4.rowMajor(
      1 - 2 * (yy + zz), 0 + 2 * (xy - wz), 0 + 2 * (zx + wy), 0,
      0 + 2 * (xy + wz), 1 - 2 * (zz + xx), 0 + 2 * (yz - wx), 0,
      0 + 2 * (zx - wy), 0 + 2 * (yz + wx), 1 - 2 * (xx + yy), 0,
      0, 0, 0, 1
    )
  }

  plus(that: Quat): Quat {
    return new Quat(this.w + that.w, this.x + that.x, this.y + that.y, this.z + that.z)
  }

  times(that: Quat): Quat {
    const { w: qw, x: qx, y: qy, z: qz } = this
    const { w: rw, x: rx, y: ry, z: rz } = that
    return new Quat(
      qw * rw - qx * rx - qy * ry - qz * rz,
      qw * rx + qx * rw + qy * rz - qz * ry,
      qw * ry - qx * rz + qy * rw + qz * rx,
      qw * rz + qx * ry - qy * rx + qz * rw
    )
  }

  timesScalar(that: number): Quat {
    return new Quat(this.w * that, this.x * that, this.y * that, this.z * that)
  }

  dot(that: Quat): number {
    return this.w * that.w + this.x * that.x + this.y * that.y + this.z * that.z
  }

  static slerp(q1: Quat, q2: Quat, u: number): Quat {
    // Animation Rotation with Quaternion Curves
    // Ken Shoemake
    // https://www.cs.cmu.edu/~kiranb/animation/p245-shoemake.pdf

    const angle = angleBetweenQuats(q1, q2)
    let t2: number
    let t1: number
    if (angle < 0.01 || angle > 0.99 * Math.PI) {
      // Tiny angle: just lerp.
      // Nearly π angle: unstable and there's no good way to interpolate. Just lerp.
      t1 = 1 - u
      t2 = u
    } else {
      const inverseSin = 1 / Math.sin(angle)
      t1 = Math.sin(angle * (1 - u)) * inverseSin
      t2 = Math.sin(angle * u) * inverseSin
    }
    return q1.timesScalar(t1)
      .plus(q2.timesScalar(t2))
  }
}

/** Return the angle between the axes of the given rotation quaternions. The angle is between 0 and π. */
function angleBetweenQuats(q0: Quat, q1: Quat): number {
  // Miscalculating Area and Angles of a Needle-like Triangle
  // William Kahan
  // https://people.eecs.berkeley.edu/~wkahan/Triangle.pdf

  let a = Math.hypot(q0.w, q0.x, q0.y, q0.z)
  let b = Math.hypot(q1.w, q1.x, q1.y, q1.z)
  let c = Math.hypot(q1.w - q0.w, q1.x - q0.x, q1.y - q0.y, q1.z - q0.z)

  // Find the angle C subtended by the side of length c.

  if (a < b) {
    [a, b] = [b, a]
  }

  let mu: number
  if (b >= c && c >= 0) {
    mu = c - (a - b)
  } else {
    mu = b - (a - c)
  }

  const n = ((a - b) + c) * mu
  if (n === 0) {
    // Catch the degenerate case that q0 === q1 === identity, which would be NaN if I don't early return here.
    return 0
  }

  const d = (a + (b + c)) * ((a - c) + b)
  return 2 * Math.atan(Math.sqrt(n / d))
}

if (import.meta.vitest) {
  const { describe, it, expect } = import.meta.vitest

  describe('angleBetweenQuats', () => {
    it('answers zero', () => {
      const q0 = Quat.rotation(vec3.xPlus, 0.5 * Math.PI)
      const q1 = Quat.rotation(vec3.xPlus, 0.5 * Math.PI)
      expect(angleBetweenQuats(q0, q1)).toEqual(expect.closeTo(0, 7))
    })

    it('answers π/4', () => {
      const q0 = Quat.rotation(vec3.xPlus, 0.5 * Math.PI)
      const q1 = Quat.rotation(vec3.xPlus.plus(vec3.yPlus), 0.5 * Math.PI)
      expect(angleBetweenQuats(q0, q1)).toEqual(expect.closeTo(0.25 * Math.PI, 7))
    })

    it('answers π/2', () => {
      const q0 = Quat.rotation(vec3.xPlus, 0.5 * Math.PI)
      const q1 = Quat.rotation(vec3.yPlus, 0.5 * Math.PI)
      expect(angleBetweenQuats(q0, q1)).toEqual(expect.closeTo(0.5 * Math.PI, 7))
    })

    it('answers 3π/4', () => {
      const q0 = Quat.rotation(vec3.xPlus, 0.5 * Math.PI)
      const q1 = Quat.rotation(vec3.xMinus.plus(vec3.yMinus), 0.5 * Math.PI)
      expect(angleBetweenQuats(q0, q1)).toEqual(expect.closeTo(0.75 * Math.PI, 7))
    })
    it('answers π', () => {
      const q0 = Quat.rotation(vec3.xPlus, 0.5 * Math.PI)
      const q1 = Quat.rotation(vec3.xMinus, 0.5 * Math.PI)
      expect(angleBetweenQuats(q0, q1)).toEqual(expect.closeTo(Math.PI, 7))
    })
  })
}
